/**
 * Evidence + manual limits for the per-device format ceiling (formatCeiling.ts).
 *
 * Paul, Oct 21 2026 — HEOS Amp "Kitchen" (and Dining Room):
 *  1. A manual Sample Rate / Bit Depth limit set in Devices ALWAYS caps the Qobuz tier the web
 *     requests, on every path (play, queue, SetNext prefetch, zone move). The untested 16/44
 *     default is not a manual limit — only a profile carrying the MANUAL_LIMIT_KEY marker is.
 *  2. A timeout is not automatically "not format": when the device demonstrably fetched audio
 *     (proxy fetch event, or the connector's play_unconfirmed / renderer-rejected wording), the
 *     failure is the hi-res-rejection signature and earns the one lower-tier replay.
 */

/** Reserved test_results key written when the owner sets a limit by hand in Devices. */
export const MANUAL_LIMIT_KEY = 'manual_format_limit';

/** Qobuz reports sampling_rate in kHz (96, 44.1); the connector and profiles in Hz. */
export function normalizeSampleRateHz(rate: number): number {
  const r = Number(rate) || 0;
  return r > 0 && r < 1000 ? Math.round(r * 1000) : r;
}

/** Qobuz format ids, highest first: 27 = up to 24/192, 7 = up to 24/96, 6 = CD 16/44.1. */
export const FORMAT_LADDER = [27, 7, 6] as const;

export function rank(formatId: number): number {
  const i = FORMAT_LADDER.indexOf(formatId as (typeof FORMAT_LADDER)[number]);
  return i < 0 ? -1 : FORMAT_LADDER.length - i;
}

/** True when the stream is above CD quality (rate > 48 kHz or depth > 16 bits). */
export function isHiResSource(formatId: number, sampleRate: number, bitDepth: number): boolean {
  sampleRate = normalizeSampleRateHz(sampleRate);
  if (sampleRate > 0 || bitDepth > 0) return sampleRate > 48_000 || bitDepth > 16;
  return formatId === 27 || formatId === 7;
}

/** Next lower tier for a failed hi-res stream: above 96 kHz → 24/96 (7), otherwise CD (6). */
export function nextLowerFormatId(formatId: number, sampleRate: number): number | null {
  if (normalizeSampleRateHz(sampleRate) > 96_000 && rank(formatId) > rank(7)) return 7;
  if (rank(formatId) > rank(6)) return 6;
  return null;
}

/** Qobuz tier that fits within a max sample rate / bit depth (null = no cap needed). */
export function formatIdForZoneLimit(maxSampleRate: number, maxBitDepth: number): number | null {
  const rate = normalizeSampleRateHz(maxSampleRate);
  const depth = Number(maxBitDepth) || 0;
  if ((rate > 0 && rate <= 48_000) || (depth > 0 && depth <= 16)) return 6;
  if (rate > 0 && rate <= 96_000) return 7;
  return null;
}

export type ZoneLimitProfile = {
  zone_id: string;
  max_sample_rate?: number | null;
  max_bit_depth?: number | null;
  test_results?: Record<string, unknown> | null;
};

/** The manual cap a zone profile carries, or null when its limit is only the untested default. */
export function readManualZoneCap(profile: ZoneLimitProfile | null | undefined): number | null {
  const marker = profile?.test_results?.[MANUAL_LIMIT_KEY] as { status?: string } | undefined;
  if (!marker || typeof marker !== 'object' || marker.status !== 'manual') return null;
  return formatIdForZoneLimit(Number(profile?.max_sample_rate) || 0, Number(profile?.max_bit_depth) || 0);
}

/** test_results with the manual-limit marker added (Devices → Sample Rate / Bit Depth change). */
export function withManualLimitMarker(results: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return { ...(results || {}), [MANUAL_LIMIT_KEY]: { status: 'manual', set_at: new Date().toISOString() } };
}

let manualCaps = new Map<string, number>();

export function hydrateManualZoneCaps(zones: ZoneLimitProfile[]): void {
  const next = new Map(manualCaps);
  for (const z of zones) {
    if (!z?.zone_id) continue;
    const cap = readManualZoneCap(z);
    if (cap == null) next.delete(z.zone_id);
    else next.set(z.zone_id, cap);
  }
  manualCaps = next;
}

export function getManualZoneCap(rendererId: string | null | undefined): number | null {
  return rendererId ? manualCaps.get(rendererId) ?? null : null;
}

export function _resetEvidenceForTests(): void {
  manualCaps = new Map();
  lastFetch.clear();
}

// ---------------------------------------------------------------------------
// Device-fetched-audio evidence
// ---------------------------------------------------------------------------

type FetchPayload = { renderer_id?: string; renderer_ip?: string } | null | undefined;
type RendererRef = { id: string; ip?: string; host?: string; location?: string };

/** Last proxy fetch per renderer id / ip (ms epoch). */
const lastFetch = new Map<string, number>();

/** Connector proxy_stream_request / proxy_stream_complete: the device pulled audio from us. */
export function noteRendererFetch(payload: FetchPayload, now = Date.now()): void {
  if (payload?.renderer_id) lastFetch.set(`id:${payload.renderer_id}`, now);
  if (payload?.renderer_ip) lastFetch.set(`ip:${payload.renderer_ip}`, now);
  if (!payload?.renderer_id && !payload?.renderer_ip) lastFetch.set('any', now);
}

export function rendererFetchedSince(renderer: RendererRef, since: number): boolean {
  const ip = renderer.ip || renderer.host || String(renderer.location || '').match(/\/\/([\d.]+)/)?.[1] || '';
  const keys = [`id:${renderer.id}`, ip ? `ip:${ip}` : '', 'any'].filter(Boolean);
  return keys.some((k) => (lastFetch.get(k) ?? 0) >= since);
}

let listenersStarted = false;
export function ensureRendererFetchListeners(): void {
  if (listenersStarted) return;
  listenersStarted = true;
  void import('../lib/connector')
    .then(async (C) => {
      try { await C.onProxyStreamRequest((p) => noteRendererFetch(p)); } catch { /* no connector */ }
      try { await C.onProxyStreamComplete((p) => noteRendererFetch(p)); } catch { /* no connector */ }
    })
    .catch(() => {});
}

/** Timeouts: learnable only when the device demonstrably fetched audio first. */
export const TIMEOUT_MARKERS = ['command timeout', 'invoke budget exceeded', 'timed out', 'ipc invoke timeout'];
/** Connector wording for "accepted the URI, fetched, then stopped without PLAYING". */
const REJECTION_MARKERS = ['play_unconfirmed', 'stream_rejected', 'renderer rejected stream', 'state=stopped'];

export function hasRejectionSignature(msg: string): boolean {
  const m = String(msg || '').toLowerCase();
  return REJECTION_MARKERS.some((k) => m.includes(k));
}
